/** /notifications/design-imports/proposals/:id — Composition Proposal review
 * (Figma plan §7 + §12.5).
 *
 * The reviewable unit of a design import: typed entity drafts (items) with
 * their evidence VERBATIM, blocking questions routed to permission holders,
 * per-item accept / reject / edit, and the trust device — the rehosted design
 * PNG side-by-side with the compiled body (§7; the full-context test render
 * becomes available once the proposal is applied and a live notification
 * exists). Apply is gated: open blocking questions 409 both here and in the
 * engine.
 */
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { isActiveState } from '@/api/bgtasks';
import { ConsoleApiError } from '@/api/client';
import * as proposalsApi from '@/api/compositionProposals';
import type {
  ProposalDetail,
  ProposalItem,
  ProposalQuestion,
} from '@/api/compositionProposals';
import { AppDialog } from '@/components/AppDialog';
import { FieldHelp } from '@/components/FieldHelp';
import { PageHeader } from '@/components/PageHeader';
import { operatorActions } from '@/components/ValueActions/identity';
import {
  act,
  DetailSubtitle,
  ValueActions,
  type ValueAction,
} from '@/components/ValueActions/ValueActions';
import { useSnackbar } from '@/components/Snackbar';
import { RenderedBodyView } from '@/features/notifications/test-render/RenderedBodyView';
import {
  attributeActions,
  bgtaskActions,
  codenameUsageActions,
  eventActions,
  proposalTargetActions,
  tagCategoryCodeActions,
  userActions,
} from '@/features/notifications/notificationLinks';
import { ScaledEmailPreview } from './ScaledEmailPreview';
import { usePermissions } from '@/hooks/usePermissions';

import { CompositionResultPanel } from './CompositionResultPanel';
import { FailedApplyBanner } from './FailedApplyBanner';
import { ItemEditDialog } from './ItemEditDialog';
import { QUESTION_KIND_HELP } from './fieldHelp';

const STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default',
  review: 'info',
  applying: 'warning',
  applied: 'success',
  rejected: 'default',
  failed: 'error',
};

/** Two-level sticky headers: the section band ("Questions" / "Proposed
 *  changes") docks at the top of the scroll pane; the CURRENT group header
 *  ("Notification: …" / "Shared components") docks right below it and is
 *  pushed out by the next group's header. Opaque background, or the cards
 *  scroll through the text. */

/** The AppShell scroll container carries p:3 (24px): content scrolls through
 *  that padding strip, so a top:0 sticky header floats 24px below the app
 *  bar with cards showing through the gap. Pin 24px higher — flush under
 *  the app bar — and stretch over the padding so nothing peeks around. */
const SCROLLER_PADDING = 24;
const SECTION_HEADER_SX = {
  position: 'sticky',
  top: -SCROLLER_PADDING,
  zIndex: 3,
  bgcolor: 'background.default',
  py: 0.75,
  mb: 0.5,
} as const;
/** Height of the section band above — group headers dock beneath it. */
const GROUP_HEADER_TOP = 44 - SCROLLER_PADDING;
const GROUP_HEADER_SX = {
  position: 'sticky',
  top: GROUP_HEADER_TOP,
  zIndex: 2,
  bgcolor: 'background.default',
  py: 0.5,
  mb: 0.5,
} as const;

/** What applying the item does to its row. */
const ACTION_CHIP: Record<string, { label: string; color: 'default' | 'warning' | 'error' | 'info' }> = {
  create: { label: 'New', color: 'default' },
  update: { label: 'Update', color: 'warning' },
  delete: { label: 'Delete', color: 'error' },
  noop: { label: 'Reuse', color: 'info' },
};

const ITEM_STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  proposed: 'default',
  accepted: 'info',
  rejected: 'default',
  applied: 'success',
  failed: 'error',
};

function pretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Group items visually: one section per imported notification (its delivery
 *  method + design follow their $item chain), then everything else — blocks,
 *  tag categories, themes — as SHARED components proposed once for the whole
 *  batch. */
function groupItems(proposal: ProposalDetail): {
  key: string;
  title: string;
  subtitle?: string;
  items: ProposalItem[];
}[] {
  const refId = (value: unknown): number | null => {
    if (typeof value === 'number') return value;
    if (value && typeof value === 'object' && '$item' in value) {
      return Number((value as { $item: unknown }).$item);
    }
    return null;
  };
  const grouped = new Set<number>();
  const notificationGroups = proposal.items
    .filter((i) => i.kind === 'notification')
    .map((nf) => {
      grouped.add(nf.id);
      const dms = proposal.items.filter(
        (i) => i.kind === 'delivery_method' && refId(i.payload.notification) === nf.id,
      );
      dms.forEach((d) => grouped.add(d.id));
      const dmIds = new Set(dms.map((d) => d.id));
      const designs = proposal.items.filter(
        (i) => i.kind === 'design' && dmIds.has(refId(i.payload.delivery_method) ?? -1),
      );
      designs.forEach((d) => grouped.add(d.id));
      return {
        key: `nf-${nf.id}`,
        title: `Notification: ${String(nf.payload.name ?? `#${nf.id}`)}`,
        items: [nf, ...dms, ...designs],
      };
    });
  const shared = proposal.items.filter((i) => !grouped.has(i.id));
  const groups: { key: string; title: string; subtitle?: string; items: ProposalItem[] }[] = [
    ...notificationGroups,
  ];
  if (shared.length > 0) {
    groups.push({
      key: 'shared',
      title: 'Shared components',
      subtitle:
        'Blocks, tag categories and the theme are proposed once and used by every imported notification.',
      items: shared,
    });
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Question card (§12.5: options + free text + discuss; answering is gated by
// required_permission; discussion supersedes, never rewrites)
// ---------------------------------------------------------------------------

function QuestionCard({
  proposal,
  question,
  canManage,
  onChanged,
}: {
  proposal: ProposalDetail;
  question: ProposalQuestion;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  // Preselect: the stored answer when there is one, else the backend's
  // recommended option (exactly one per question carries the flag).
  const answeredIndex =
    typeof (question.answer as { option?: unknown } | null)?.option === 'number'
      ? ((question.answer as { option: number }).option)
      : null;
  const recommendedIndex = (question.options ?? []).findIndex((o) => o.recommended);
  const [option, setOption] = useState<number | null>(
    () => answeredIndex ?? (recommendedIndex >= 0 ? recommendedIndex : null),
  );
  const [freeText, setFreeText] = useState('');
  const [editing, setEditing] = useState(false);
  const [discussOpen, setDiscussOpen] = useState(false);
  const [discussText, setDiscussText] = useState('');

  const answerMut = useMutation({
    mutationFn: () =>
      proposalsApi.answerQuestion(proposal.id, question.id, {
        ...(option !== null ? { option } : {}),
        ...(freeText.trim() ? { free_text: freeText.trim() } : {}),
      }),
    onSuccess: () => {
      snackbar.show('Answer recorded.', 'success');
      setEditing(false);
      onChanged();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Answering failed.', 'error'),
  });

  const discussMut = useMutation({
    mutationFn: () => proposalsApi.discussQuestion(proposal.id, question.id, { text: discussText.trim() }),
    onSuccess: () => {
      snackbar.show('Discussion recorded — a successor question replaces this one.', 'success');
      setDiscussOpen(false);
      setDiscussText('');
      onChanged();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Discussion failed.', 'error'),
  });

  const superseded = question.superseded_by !== null;
  const answered = question.answer !== null;
  // Answers stay editable while the proposal is in review ("Change answer");
  // only superseded questions are frozen.
  const readOnly = superseded || proposal.status !== 'review' || !canManage;
  const permBlocked = !question.can_answer && !superseded;
  const showControls = !superseded && (!answered || editing);

  // Informational notes (no options, no free text, no discussion — e.g.
  // "these attributes have no data source yet") are notices, not decisions:
  // no Answer button, nothing expected from the reviewer.
  if (!question.options?.length && !question.allow_free_text && !question.allow_discussion) {
    return (
      <Alert severity="info" sx={{ mb: 1.5 }} id={`question-${question.id}`}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {question.prompt}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Informational — nothing to answer here. It does not block applying the proposal.
        </Typography>
      </Alert>
    );
  }

  return (
    <Paper
      variant="outlined"
      id={`question-${question.id}`}
      sx={{ p: 1.5, mb: 1.5, opacity: superseded ? 0.55 : 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {question.blocking && !answered && !superseded && (
          <Chip size="small" color="warning" label="blocking" />
        )}
        {question.kind && <Chip size="small" variant="outlined" label={question.kind} />}
        {QUESTION_KIND_HELP[question.kind] && <FieldHelp help={QUESTION_KIND_HELP[question.kind]!} />}
        {question.required_permission && (
          <Tooltip title={`Answering requires ${question.required_permission}`}>
            <Chip
              size="small"
              variant="outlined"
              color={question.can_answer ? 'default' : 'warning'}
              label={question.required_permission}
            />
          </Tooltip>
        )}
        {superseded && (
          <Chip size="small" variant="outlined" label={`superseded by #${question.superseded_by}`} />
        )}
      </Box>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
        {question.prompt}
      </Typography>

      {answered && !editing && (
        <Alert
          severity="success"
          icon={<CheckIcon fontSize="small" />}
          action={
            canManage && proposal.status === 'review' && question.can_answer ? (
              <Button size="small" color="inherit" onClick={() => setEditing(true)}>
                Change answer
              </Button>
            ) : undefined
          }
        >
          {String((question.answer as { label?: string } | null)?.label ?? '')}
          {(question.answer as { free_text?: string } | null)?.free_text
            ? ` — “${(question.answer as { free_text?: string }).free_text}”`
            : ''}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {question.answered_by && (
              <ValueActions actions={userActions(question.answered_by)}>
                {question.answered_by.email}
              </ValueActions>
            )}
            {question.answered_ts ? ` · ${new Date(question.answered_ts).toLocaleString()}` : ''}
          </Typography>
        </Alert>
      )}
      {showControls && (
        <>
          {(question.options ?? []).length > 0 && (
            <RadioGroup value={option ?? ''} onChange={(e) => setOption(Number(e.target.value))}>
              {(question.options ?? []).map((opt, i) => (
                <FormControlLabel
                  key={i} // eslint-disable-line react/no-array-index-key
                  value={i}
                  disabled={readOnly || permBlocked}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2">{opt.label}</Typography>
                      {opt.recommended && (
                        <Chip size="small" variant="outlined" color="success" label="recommended" />
                      )}
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          )}
          {question.allow_free_text && (
            <TextField
              fullWidth
              size="small"
              label="Something else… (free text)"
              value={freeText}
              disabled={readOnly || permBlocked}
              onChange={(e) => setFreeText(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Tooltip title={permBlocked ? `Requires ${question.required_permission}` : ''}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={readOnly || permBlocked || (option === null && !freeText.trim()) || answerMut.isPending}
                  onClick={() => answerMut.mutate()}
                >
                  Answer
                </Button>
              </span>
            </Tooltip>
            {editing && (
              <Button size="small" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            )}
            {question.allow_discussion && (
              <Button
                size="small"
                disabled={proposal.status !== 'review' || !canManage || discussMut.isPending}
                onClick={() => setDiscussOpen(true)}
              >
                Let’s talk about it
              </Button>
            )}
          </Box>
        </>
      )}

      <AppDialog
        open={discussOpen}
        onClose={() => setDiscussOpen(false)}
        size="sm"
        title="Discuss this question"
        actions={
          <>
            <Button onClick={() => setDiscussOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!discussText.trim() || discussMut.isPending}
              onClick={() => discussMut.mutate()}
            >
              Send
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Discussion refines, it does not resolve: your message creates a NEW question superseding this
          one, and the original stays as provenance.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label="Your message"
          value={discussText}
          onChange={(e) => setDiscussText(e.target.value)}
        />
      </AppDialog>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Item card — accept / reject / edit, evidence verbatim, design trust device
// ---------------------------------------------------------------------------

/** One human sentence saying WHAT this draft is — the JSON stays behind the
 *  "raw details" toggle for the curious. */
function itemHeadline(item: ProposalItem): string {
  const p = item.payload;
  switch (item.kind) {
    case 'notification':
      return `Notification “${String(p.name ?? '')}” on event ${String(p.event ?? '?')} — created disabled.`;
    case 'delivery_method':
      return `Email delivery — subject: “${String(p.email_subject ?? '')}”.`;
    case 'design':
      return `Email body design (compiled below; the source document travels with it).`;
    case 'block':
      return p.reused
        ? `Reuses the existing block “${String(p.codename ?? '')}” — nothing new is minted.`
        : `New content block “${String(p.codename ?? '')}”.`;
    case 'tag_category': {
      const attrs = Array.isArray(p.attributes) ? p.attributes.length : 0;
      return `${p.new_category ? 'New tag category' : 'Tag category'} “${String(p.category_code ?? '')}” — ${attrs} attribute${attrs === 1 ? '' : 's'} the design reads.`;
    }
    case 'theme':
      return `Email theme “${String(p.codename ?? '')}” — colours + shell extracted from the design.`;
    case 'integration_config':
      return 'Suggested integration mapping (informational — configure it on the integration, it cannot be applied from here).';
    default:
      return item.kind.replace(/_/g, ' ');
  }
}

/** The records a draft item names: what it UPDATES (when it updates one), the
 *  codename it will mint or reuse, the event it fires on, the tag category it
 *  reads. None of these were reachable — the item headline is one sentence and
 *  the ids live in the payload.
 *
 *  A `create` item has no `target_id` by definition, and `delivery_method` has
 *  no console page of its own, so those render nothing rather than a link that
 *  would 404. */
function ItemReferences({ item, tenantId }: { item: ProposalItem; tenantId: number }) {
  const p = item.payload;
  const codename = typeof p.codename === 'string' ? p.codename : null;
  const event = typeof p.event === 'string' ? p.event : null;
  const categoryCode = typeof p.category_code === 'string' ? p.category_code : null;

  const chips: { key: string; label: string; actions: ValueAction[] }[] = [];

  if (item.action === 'update' && item.target_id != null) {
    const actions = proposalTargetActions(item.kind, item.target_id);
    if (actions.some((a) => 'href' in a)) {
      chips.push({ key: 'target', label: `updates #${item.target_id}`, actions });
    }
  }
  if (codename && (item.kind === 'block' || item.kind === 'theme')) {
    chips.push({
      key: 'codename',
      label: codename,
      actions: codenameUsageActions(item.kind === 'block' ? 'block' : 'theme', codename),
    });
  }
  if (event) chips.push({ key: 'event', label: event, actions: eventActions(event, { tenantId }) });
  if (categoryCode) {
    chips.push({
      key: 'category',
      label: categoryCode,
      actions: tagCategoryCodeActions(categoryCode, { tenantId }),
    });
  }

  if (chips.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
      {chips.map((c) => (
        <ValueActions chip key={c.key} actions={c.actions}>
          <Chip size="small" variant="outlined" label={c.label} sx={{ fontFamily: 'monospace' }} />
        </ValueActions>
      ))}
    </Box>
  );
}

function ItemCard({
  proposal,
  item,
  canManage,
  onChanged,
}: {
  proposal: ProposalDetail;
  item: ProposalItem;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const [showEvidence, setShowEvidence] = useState(false);
  const [showBindings, setShowBindings] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const evidence = (item.evidence ?? {}) as Record<string, unknown>;
  const bindings = Array.isArray(evidence.bindings)
    ? (evidence.bindings as Array<{ text: string; path: string; source: string; confidence: number }>)
    : [];
  const unboundSpans = Array.isArray(evidence.unbound)
    ? (evidence.unbound as Array<{ text: string; reason: string }>)
    : [];

  const mutation = useMutation({
    mutationFn: (body: { status?: 'proposed' | 'accepted' | 'rejected'; payload?: Record<string, unknown> }) =>
      proposalsApi.updateItem(proposal.id, item.id, body),
    onSuccess: () => onChanged(),
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Item update failed.', 'error'),
  });

  const reviewable = proposal.status === 'review' && canManage && ['proposed', 'accepted', 'rejected'].includes(item.status);
  const isDesign = item.kind === 'design';
  const previewImage = (item.evidence as { preview_image?: string } | null)?.preview_image;
  const applyError = (item.evidence as { apply_error?: string } | null)?.apply_error;
  const attributes = Array.isArray(item.payload.attributes)
    ? (item.payload.attributes as { attribute?: string }[])
    : [];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={item.kind} />
        <Chip
          size="small"
          variant="outlined"
          color={ACTION_CHIP[item.action]?.color ?? 'default'}
          label={ACTION_CHIP[item.action]?.label ?? 'New'}
        />
        <Chip size="small" color={ITEM_STATUS_COLOR[item.status] ?? 'default'} label={item.status} />
        {!item.appliable && (
          <Tooltip title="Reserved kind — review information only; no materializer applies it in this phase.">
            <Chip size="small" variant="outlined" color="warning" label="review-only" />
          </Tooltip>
        )}
        {item.confidence != null && (
          <Typography variant="caption" color="text.secondary">
            confidence {Math.round(item.confidence * 100)}%
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {reviewable && (
          <>
            {item.status !== 'proposed' && (
              <Tooltip title="Undo — back to proposed (no decision)">
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<UndoIcon fontSize="small" />}
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ status: 'proposed' })}
                >
                  Undo
                </Button>
              </Tooltip>
            )}
            <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button
              size="small"
              color="inherit"
              startIcon={<CloseIcon fontSize="small" />}
              disabled={item.status === 'rejected' || mutation.isPending}
              onClick={() => mutation.mutate({ status: 'rejected' })}
            >
              Reject
            </Button>
            <Tooltip title={!item.appliable ? 'Reserved kinds cannot be accepted.' : ''}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckIcon fontSize="small" />}
                  disabled={!item.appliable || item.status === 'accepted' || mutation.isPending}
                  onClick={() => mutation.mutate({ status: 'accepted' })}
                >
                  Accept
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      <Typography variant="body2" sx={{ mt: 1 }}>
        {itemHeadline(item)}
      </Typography>
      <ItemReferences item={item} tenantId={proposal.tour_operator.id} />
      {attributes.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          {attributes.map((attr) => (
            <ValueActions
              chip
              key={String(attr.attribute)}
              actions={attributeActions(String(attr.attribute), {
                tenantId: proposal.tour_operator.id,
              })}
            >
              <Chip size="small" variant="outlined" label={String(attr.attribute)} />
            </ValueActions>
          ))}
        </Box>
      )}

      {applyError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Apply failed: {applyError}
        </Alert>
      )}

      {isDesign && (bindings.length > 0 || unboundSpans.length > 0) && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1, cursor: 'pointer' }}
            onClick={() => setShowBindings((v) => !v)}
          >
            {showBindings ? '▾' : '▸'} bindings ({bindings.length} mapped, {unboundSpans.length} literal)
          </Typography>
          <Collapse in={showBindings}>
            <Box sx={{ mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12 }}>
              {bindings.map((b, i) => (
                <Box key={`b-${i}`} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', py: 0.25 }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    “{b.text.length > 36 ? `${b.text.slice(0, 36)}…` : b.text}”
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    →
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'success.main' }}>
                    {b.path}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({b.source}, {Math.round((b.confidence ?? 0) * 100)}%)
                  </Typography>
                </Box>
              ))}
              {unboundSpans.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Literal (same for every guest):{' '}
                  {unboundSpans
                    .slice(0, 12)
                    .map((u) => `“${u.text.length > 24 ? `${u.text.slice(0, 24)}…` : u.text}”`)
                    .join(', ')}
                  {unboundSpans.length > 12 ? ` +${unboundSpans.length - 12} more` : ''}
                </Typography>
              )}
            </Box>
          </Collapse>
        </>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1, cursor: 'pointer' }}
        onClick={() => setShowEvidence((v) => !v)}
      >
        {showEvidence ? '▾' : '▸'} raw details (evidence & payload)
      </Typography>
      <Collapse in={showEvidence}>
        {item.evidence !== null && (
          <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, overflow: 'auto', maxHeight: 240 }}>
            {pretty(item.evidence)}
          </Box>
        )}
        <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, overflow: 'auto', maxHeight: 240 }}>
          {pretty(item.payload)}
        </Box>
      </Collapse>

      {item.kind === 'block' && item.compiled_preview && (
        <Box sx={{ maxWidth: 640, mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Block preview (placeholders highlighted)
          </Typography>
          <RenderedBodyView
            body={item.compiled_preview}
            preview
            title={`Block ${String(item.payload.codename ?? item.id)}`}
            height={220}
          />
        </Box>
      )}

      {isDesign && (
        <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
          {previewImage && (
            <Box sx={{ flex: '0 1 460px', minWidth: 300 }}>
              <Typography variant="caption" color="text.secondary">
                Design (rehosted render)
              </Typography>
              <Box
                sx={{
                  maxHeight: 720,
                  overflowY: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box
                  component="img"
                  src={previewImage}
                  alt="Design render"
                  sx={{ display: 'block', width: '100%', maxWidth: 460 }}
                />
              </Box>
            </Box>
          )}
          <Box sx={{ flex: '0 1 460px', minWidth: 300 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Compiled body (unbound tokens stay literal; full test render after apply)
            </Typography>
            {item.compiled_preview ? (
              <ScaledEmailPreview body={item.compiled_preview} title={`Design item ${item.id}`} height={720} />
            ) : (
              <Alert severity="warning">{item.compiled_preview_error ?? 'No compiled preview.'}</Alert>
            )}
          </Box>
        </Box>
      )}

      {editOpen && (
        <ItemEditDialog
          proposalId={proposal.id}
          item={item}
          onClose={() => setEditOpen(false)}
          onSaved={onChanged}
        />
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ProposalReviewPage() {
  const { id } = useParams<{ id: string }>();
  const proposalId = Number(id);
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_composition_manage');
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyReason, setApplyReason] = useState('');
  const [applyTaskId, setApplyTaskId] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['composition-proposal', proposalId],
    queryFn: () => proposalsApi.retrieve(proposalId),
    enabled: Number.isFinite(proposalId),
    refetchInterval: (q) => (q.state.data?.status === 'applying' ? 2_500 : false),
  });

  const applyTask = useQuery({
    queryKey: ['bgtask', applyTaskId],
    queryFn: () => bgtasksApi.retrieve(applyTaskId!),
    enabled: !!applyTaskId,
    refetchInterval: (q) => {
      if (q.state.data && !isActiveState(q.state.data.state)) {
        void qc.invalidateQueries({ queryKey: ['composition-proposal', proposalId] });
        return false;
      }
      return 2_000;
    },
  });

  const applyMut = useMutation({
    mutationFn: () => proposalsApi.startApply(proposalId, applyReason.trim() ? { reason: applyReason.trim() } : {}),
    onSuccess: (started) => {
      setApplyTaskId(started.task_id);
      setApplyOpen(false);
      snackbar.show('Apply started.', 'success');
      void qc.invalidateQueries({ queryKey: ['composition-proposal', proposalId] });
    },
    onError: (e) => {
      const detailMsg =
        e instanceof ConsoleApiError ? (e.body?.detail as string | undefined) ?? e.message : 'Apply failed to start.';
      snackbar.show(detailMsg, 'error');
      setApplyOpen(false);
      void qc.invalidateQueries({ queryKey: ['composition-proposal', proposalId] });
    },
  });

  const refetch = () => void qc.invalidateQueries({ queryKey: ['composition-proposal', proposalId] });
  const [bulkBusy, setBulkBusy] = useState(false);

  /** Bulk decisions run as individual calls on purpose: each keeps its own
   *  audit row and answer delta; a partial failure is reported, not hidden. */
  const bulkSetItems = async (items: ProposalItem[], status: 'accepted' | 'rejected') => {
    const targets = items.filter(
      (i) =>
        ['proposed', 'accepted', 'rejected'].includes(i.status) &&
        i.status !== status &&
        (status !== 'accepted' || i.appliable),
    );
    if (targets.length === 0) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        targets.map((i) => proposalsApi.updateItem(proposalId, i.id, { status })),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      snackbar.show(
        `${ok} of ${targets.length} item(s) ${status}.`,
        ok === targets.length ? 'success' : 'warning',
      );
    } finally {
      setBulkBusy(false);
      refetch();
    }
  };

  const answerAllRecommended = async (questions: ProposalQuestion[]) => {
    const targets = questions
      .map((q) => ({ q, index: (q.options ?? []).findIndex((o) => o.recommended) }))
      .filter(
        ({ q, index }) => index >= 0 && q.answer === null && q.superseded_by === null && q.can_answer,
      );
    if (targets.length === 0) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        targets.map(({ q, index }) => proposalsApi.answerQuestion(proposalId, q.id, { option: index })),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const skipped = questions.filter(
        (q) => q.answer === null && q.superseded_by === null && !q.can_answer,
      ).length;
      snackbar.show(
        `${ok} of ${targets.length} question(s) answered with the recommended option.` +
          (skipped ? ` ${skipped} skipped — they require a permission you don't hold.` : ''),
        ok === targets.length ? 'success' : 'warning',
      );
    } finally {
      setBulkBusy(false);
      refetch();
    }
  };

  if (!Number.isFinite(proposalId)) {
    return <Alert severity="error">Invalid proposal id.</Alert>;
  }
  if (detail.isLoading || !detail.data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        {detail.isError ? <Alert severity="error">Proposal not found.</Alert> : <CircularProgress />}
      </Box>
    );
  }
  const proposal = detail.data;
  const openBlocking = proposal.open_blocking_questions;
  const accepted = proposal.items.filter((i) => i.status === 'accepted').length;
  const applying = proposal.status === 'applying' || (applyTask.data ? isActiveState(applyTask.data.state) : false);

  return (
    <Box sx={{ pb: 10 }}>
      <PageHeader
        helpStory="design-imports"
        title={`Proposal #${proposal.id}`}
        subtitle={
          <DetailSubtitle
            parts={[
              <ValueActions key="tenant" actions={operatorActions(proposal.tour_operator)}>
                {proposal.tour_operator.code || proposal.tour_operator.name}
              </ValueActions>,
              `${proposal.origin} import`,
              proposal.materials.length ? (
                <span key="materials">
                  {proposal.materials.map((m, i) => (
                    <span key={m.id}>
                      {i > 0 && ', '}
                      {m.url ? (
                        <ValueActions
                          actions={[
                            act.external(m.url, `Open the ${m.kind} source`),
                            act.copy(m.url, 'Copy source URL'),
                          ]}
                        >
                          {m.title}
                        </ValueActions>
                      ) : (
                        m.title
                      )}
                    </span>
                  ))}
                </span>
              ) : (
                'no material'
              ),
              <ValueActions key="id" actions={[act.copy(String(proposal.id), 'Copy id')]}>ID {proposal.id}</ValueActions>,
            ]}
          />
        }
        actions={
          <Chip color={STATUS_COLOR[proposal.status] ?? 'default'} label={proposal.status} sx={{ fontWeight: 600 }} />
        }
      />

      {applying && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Applying accepted items…
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {proposal.status === 'applied' && (applyTaskId ?? proposal.apply_task_id) && (
        // The apply receipt: what was created/updated, each linked to its
        // edit surface. Shown on any visit — the task id comes back with
        // the proposal.
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {/* The receipt is archived on the apply task — which nothing on
                this page linked to. */}
            <ValueActions actions={bgtaskActions((applyTaskId ?? proposal.apply_task_id)!)}>
              Applied
            </ValueActions>
          </Typography>
          <CompositionResultPanel taskId={(applyTaskId ?? proposal.apply_task_id)!} hideProposalLink />
        </Paper>
      )}

      {proposal.status === 'review' && openBlocking.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {openBlocking.length} blocking question{openBlocking.length === 1 ? '' : 's'} must be answered
          before this proposal can be applied.
        </Alert>
      )}
      {proposal.status === 'failed' && (
        <FailedApplyBanner
          proposalId={proposal.id}
          canManage={canManage}
          onReturned={refetch}
          applyTaskId={applyTaskId ?? proposal.apply_task_id}
        />
      )}

      {proposal.questions.length > 0 && (
        // Each section owns a wrapper Box so its sticky header RELEASES when
        // the section scrolls out, handing over to the next section's header.
        <Box>
          <Box sx={{ ...SECTION_HEADER_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Questions</Typography>
            <Box sx={{ flexGrow: 1 }} />
            {canManage && proposal.status === 'review' && (
              <Tooltip title="Answers every open question you may answer with its recommended option — each answer stays individually changeable.">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={bulkBusy}
                    onClick={() => void answerAllRecommended(proposal.questions)}
                  >
                    Answer all (recommended)
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
          {proposal.questions.map((q) => (
            <QuestionCard key={q.id} proposal={proposal} question={q} canManage={canManage} onChanged={refetch} />
          ))}
        </Box>
      )}

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="h6" sx={SECTION_HEADER_SX}>
          Proposed changes
        </Typography>
        {groupItems(proposal).map((group) => (
          <Box key={group.key} sx={{ mb: 2.5 }}>
            <Box sx={{ ...GROUP_HEADER_SX, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {group.title}
                </Typography>
                {group.subtitle && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {group.subtitle}
                  </Typography>
                )}
              </Box>
              {canManage && proposal.status === 'review' && (
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={bulkBusy}
                    onClick={() => void bulkSetItems(group.items, 'accepted')}
                  >
                    Accept all
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={bulkBusy}
                    onClick={() => void bulkSetItems(group.items, 'rejected')}
                  >
                    Reject all
                  </Button>
                </Box>
              )}
            </Box>
            {group.items.map((item) => (
              <ItemCard key={item.id} proposal={proposal} item={item} canManage={canManage} onChanged={refetch} />
            ))}
          </Box>
        ))}
      </Box>

      <Paper
        variant="outlined"
        sx={{
          position: 'sticky',
          bottom: 0,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          zIndex: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2">
          {accepted} of {proposal.items.length} item{proposal.items.length === 1 ? '' : 's'} accepted
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={() => navigate('/notifications/design-imports')}>
          Back to Design Imports
        </Button>
        {canManage && proposal.status === 'review' && (
          <Tooltip
            title={
              openBlocking.length > 0
                ? 'Blocking questions are open.'
                : accepted === 0
                  ? 'Accept at least one item first.'
                  : ''
            }
          >
            <span>
              <Button
                variant="contained"
                disabled={openBlocking.length > 0 || accepted === 0 || applying || applyMut.isPending}
                onClick={() => setApplyOpen(true)}
              >
                Apply {accepted} accepted item{accepted === 1 ? '' : 's'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Paper>

      <AppDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        size="sm"
        title="Apply this proposal?"
        actions={
          <>
            <Button onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={applyMut.isPending} onClick={() => applyMut.mutate()}>
              Apply
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {accepted} accepted item{accepted === 1 ? '' : 's'} will be materialized for{' '}
          <strong>{proposal.tour_operator.code || proposal.tour_operator.name}</strong>. Imported
          notifications arrive DISABLED — nothing starts sending on apply.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Reason (recorded in change history)"
          value={applyReason}
          onChange={(e) => setApplyReason(e.target.value)}
        />
      </AppDialog>
    </Box>
  );
}
